<template>
  <q-dialog v-model="showDialog" persistent>
    <q-card class="q-pa-md" style="min-width: 280px; max-width: 90vw">
      <q-card-section class="text-h6">扫码查看截图</q-card-section>
      <q-card-section class="flex flex-center column q-gutter-md">
        <img
          v-if="qrDataUrl"
          :src="qrDataUrl"
          alt="二维码"
          style="width: 256px; height: 256px; image-rendering: pixelated"
        />
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat label="关闭" color="primary" v-close-popup />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useQuasar } from "quasar";
import { toBlob, getFontEmbedCSS } from "html-to-image";
import { upload } from "@vercel/blob/client";
import QRCode from "qrcode";

const props = defineProps<{
  /** 不传则截取 #app；避免截到 documentElement 时把 Quasar $q.loading 等挂 body 的遮罩一并截进去 */
  captureTarget?: HTMLElement | null;
}>();

function getCaptureElement(): HTMLElement {
  const custom = props.captureTarget;
  if (custom) {
    return custom;
  }
  const app = document.querySelector("#app");
  if (app instanceof HTMLElement) {
    return app;
  }
  return document.body;
}

const route = useRoute();
const $q = useQuasar();

/** 路由 path 按 / 切分后用 - 连接，并去掉不适合 Blob pathname 的字符 */
function routePathToSlug(path: string): string {
  const joined =
    path
      .replace(/^\/+|\/+$/g, "")
      .split("/")
      .filter(Boolean)
      .join("-") || "home";
  return joined
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "home";
}

/** 本地时间：YYYY-MM-DD_HH-mm */
function formatTimestampForFilename(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}_${h}-${min}`;
}

const showDialog = ref(false);
const qrDataUrl = ref("");

/** 使用 html-to-image：嵌入 @font-face 后 SVG 导出，图标与文字较接近真机 */
async function captureElementToPngBlob(el: HTMLElement): Promise<Blob> {
  await document.fonts.ready.catch(() => undefined);
  const scale = Math.min(2, window.devicePixelRatio || 1);

  const fontEmbedCSS = await getFontEmbedCSS(el, {
    cacheBust: true,
  }).catch(() => undefined);

  const blob = await toBlob(el, {
    pixelRatio: scale,
    cacheBust: true,
    type: "image/png",
    ...(fontEmbedCSS ? { fontEmbedCSS } : {}),
  });

  if (blob == null || blob.size === 0) {
    throw new Error("截图生成失败（空结果）");
  }
  return blob;
}

async function captureAndUpload(): Promise<void> {
  const el = getCaptureElement();

  const handleUploadUrl = import.meta.env.VITE_BLOB_HANDLE_UPLOAD_URL as
    | string
    | undefined;
  if (!handleUploadUrl?.trim()) {
    const msg = "未配置 VITE_BLOB_HANDLE_UPLOAD_URL";
    $q.notify({ type: "negative", message: msg });
    throw new Error(msg);
  }

  try {
    const blob = await captureElementToPngBlob(el);
    const slug = routePathToSlug(route.path);
    const basename = `x11-previewinfo-${slug}-screenshot-${formatTimestampForFilename()}.png`;
    const pathname = `x11-previewinfo-screenshot/${basename}`;
    const file = new File([blob], basename, { type: "image/png" });

    const result = await upload(pathname, file, {
      access: "public",
      handleUploadUrl: handleUploadUrl.trim(),
      contentType: "image/png",
    });

    qrDataUrl.value = await QRCode.toDataURL(result.url, {
      width: 256,
      margin: 2,
    });
    showDialog.value = true;
  } catch (e) {
    const msg = e instanceof Error ? e.message : "截图或上传失败";
    $q.notify({ type: "negative", message: msg });
    throw e;
  }
}

defineExpose({
  captureAndUpload,
});
</script>
